import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2, Crown, UserRound, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const SESSION_KEY = "wte_ai_session";
const SUGGESTIONS = [
  "Canada ou Australie : que me conseillez-vous ?",
  "Quel visa pour travailler au Canada à 30 ans ?",
  "Que contient le Pack Visa ?",
  "Comment se passe l'accompagnement ?",
];

const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = `wte-${Math.random().toString(36).slice(2)}-${Date.now()}`; localStorage.setItem(SESSION_KEY, s); }
  return s;
};

export const WteAiChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ premium: false });
  const endRef = useRef(null);
  const session = getSession();

  useEffect(() => {
    if (!open) return;
    api.get("/ai/status").then((r) => setStatus(r.data)).catch(() => {});
    if (messages.length === 0) {
      api.get(`/ai/history/${session}`).then(({ data }) => {
        setMessages(data.length ? data : [{
          role: "assistant",
          content: "Bonjour 👋 Je suis WTE AI, l'assistant de Work & Travel Expert. Dites-moi votre projet (Australie ou Canada, votre âge, votre situation) et je vous oriente. Je distingue toujours les informations officielles de mes explications, et je ne garantis jamais l'obtention d'un visa.",
        }]);
      }).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: session, message: content });
      setMessages((m) => [...m, { role: "assistant", content: data.content, limit: data.limit_reached }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Je suis momentanément indisponible. Vous pouvez réserver une consultation gratuite de 30 minutes avec un conseiller — je vous y emmène quand vous voulez.",
      }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      {/* Lanceur */}
      <button
        data-testid="wte-ai-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir WTE AI"
        className="fixed bottom-5 left-5 z-[9997] inline-flex items-center gap-2 rounded-full border border-gold/50 bg-navy/95 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-gold shadow-2xl backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-navy"
      >
        <Sparkles size={16} /> WTE AI
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="wte-ai-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-4 left-3 right-3 z-[9999] flex h-[78vh] flex-col overflow-hidden rounded-2xl border border-gold/30 bg-navy shadow-2xl sm:bottom-6 sm:left-6 sm:right-auto sm:h-[600px] sm:w-[400px]"
          >
            {/* En-tête */}
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <p className="flex items-center gap-2 font-heading text-lg text-white">
                  <Sparkles size={16} className="text-gold" /> WTE AI
                  {status.premium && <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-gold"><Crown size={10} /> Premium</span>}
                </p>
                <p className="mt-0.5 text-[11px] text-white/50">Assistant disponible 24h/24 · réponses sourcées</p>
              </div>
              <button data-testid="wte-ai-close" onClick={() => setOpen(false)} className="text-white/60 transition-colors hover:text-gold"><X size={18} /></button>
            </div>

            {/* Messages */}
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="wte-ai-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-gold text-navy" : "border border-white/10 bg-white/[0.05] text-white/90"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 text-sm text-white/60">
                    <Loader2 size={14} className="animate-spin text-gold" /> WTE AI rédige…
                  </div>
                </div>
              )}
              {messages.length <= 1 && !loading && (
                <div className="space-y-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} data-testid={`wte-ai-suggestion-${SUGGESTIONS.indexOf(s)}`} onClick={() => send(s)}
                      className="block w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-left text-xs text-white/80 transition-colors hover:border-gold hover:text-gold">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Premium / conseiller */}
            <div className="flex items-center justify-between gap-2 border-t border-white/10 px-4 py-2.5 text-[11px]">
              {!status.premium ? (
                <Link to="/nos-packs?ai=premium" data-testid="wte-ai-premium-link" onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-gold hover:underline">
                  <Crown size={12} /> Premium 10 €/mois
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-white/50"><ShieldCheck size={12} className="text-gold" /> Accès Premium actif</span>
              )}
              <Link to="/rendez-vous" data-testid="wte-ai-human-link" onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1.5 text-white/60 transition-colors hover:text-gold">
                <UserRound size={12} /> Parler à un conseiller
              </Link>
            </div>

            {/* Saisie */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 border-t border-white/10 p-3">
              <textarea
                data-testid="wte-ai-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1} placeholder="Posez votre question…"
                className="max-h-24 flex-1 resize-none rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white outline-none placeholder:text-white/40 focus:border-gold"
              />
              <button type="submit" data-testid="wte-ai-send" disabled={loading}
                className="rounded-xl bg-gold p-3 text-navy transition-colors hover:bg-gold-hover disabled:opacity-50">
                <Send size={16} />
              </button>
            </form>
            <p className="px-4 pb-3 text-[10px] leading-snug text-white/35">
              WTE AI ne garantit jamais l'obtention d'un visa. Les décisions appartiennent au Department of Home Affairs (Australie) et à IRCC (Canada).
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
